/**
 * 圖片壓縮工具
 * 在前端上傳前壓縮圖片，減少網絡傳輸和後端存儲空間
 */

export interface CompressOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: string;
}

export const COMPRESS_PRESETS = {
  // 標準質量（適合一般服務照片）
  STANDARD: {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.8,
    mimeType: 'image/jpeg',
  },
  // 高質量（適合損壞照片等需要細節的照片）
  HIGH: {
    maxWidth: 2560,
    maxHeight: 2560,
    quality: 0.85,
    mimeType: 'image/jpeg',
  },
  // 低質量（適合縮略圖）
  LOW: {
    maxWidth: 1280,
    maxHeight: 1280,
    quality: 0.7,
    mimeType: 'image/jpeg',
  },
};

/**
 * 壓縮圖片文件
 * @param file 原始圖片文件
 * @param options 壓縮選項
 * @returns 壓縮後的文件
 */
export async function compressImage(
  file: File,
  options: CompressOptions = COMPRESS_PRESETS.STANDARD
): Promise<File> {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.8,
    mimeType = 'image/jpeg',
  } = options;

  return new Promise((resolve, reject) => {
    // 如果不是圖片文件，直接返回
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      reject(new Error('無法讀取圖片文件'));
    };

    reader.onload = (e) => {
      const img = new Image();

      img.onerror = () => {
        reject(new Error('無法載入圖片'));
      };

      img.onload = () => {
        // 計算新的尺寸（保持寬高比）
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        // 創建 canvas 進行壓縮
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('無法創建 canvas 上下文'));
          return;
        }

        // 繪製圖片
        ctx.drawImage(img, 0, 0, width, height);

        // 轉換為 Blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('壓縮失敗'));
              return;
            }

            // 創建新的 File 對象
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.\w+$/, '.jpg'), // 統一使用 .jpg 擴展名
              {
                type: mimeType,
                lastModified: Date.now(),
              }
            );

            resolve(compressedFile);
          },
          mimeType,
          quality
        );
      };

      img.src = e.target?.result as string;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * 批量壓縮圖片
 * @param files 圖片文件數組
 * @param options 壓縮選項
 * @returns 壓縮後的文件數組
 */
export async function compressImages(
  files: File[],
  options: CompressOptions = COMPRESS_PRESETS.STANDARD
): Promise<File[]> {
  const promises = files.map(file => compressImage(file, options));
  return Promise.all(promises);
}

